import { Link } from "react-router-dom";
import { MapPin, ArrowRight, TrendingUp, Sparkles, X } from "lucide-react";
import { useGeo } from "../lib/geo";
import { useLastEstimate, clearEstimate } from "../lib/savings";
import { useQuoteModal } from "../lib/quoteModal";
import { STATES, NATIONAL_AVG } from "../lib/states";
import { CITIES } from "../lib/cities";

function matchState(region) {
  if (!region) return null;
  const needle = String(region).trim().toLowerCase();
  return STATES.find((s) => s.name.toLowerCase() === needle) || null;
}

function matchCity(city, stateSlug) {
  if (!city || !stateSlug) return null;
  const needle = String(city).trim().toLowerCase();
  return (
    CITIES.find((c) => c.state === stateSlug && c.name.toLowerCase() === needle) ||
    null
  );
}

function relativeTime(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  return "a while ago";
}

// CTA destination based on which calculator produced the estimate.
// Returns null when the destination should be the global Quote Modal
// (handled by an onClick handler instead of <Link>).
function ctaForType(type) {
  if (type === "refinance") return "/auto-loan-refinance-calculator";
  return null;
}

function ctaLabel(type, stateCode) {
  if (type === "refinance") return `Compare refi savings`;
  return `Compare ${stateCode} offers`;
}

// Headline copy for the re-engagement state.
function reEngageHeadline(estimate, city, state) {
  const place = city ? city.name : state.name;
  const amount = `$${estimate.annual_savings.toLocaleString()}`;
  if (estimate.type === "refinance") {
    return {
      eyebrow: `Welcome back · ${place}`,
      headline: (
        <>
          Your last refi estimate was{" "}
          <span className="text-[#10B981]">{amount}/yr</span> in savings.
        </>
      ),
      sub: "Pick up where you left off — soft credit pull, no impact to your score.",
    };
  }
  return {
    eyebrow: `Welcome back · ${place}`,
    headline: (
      <>
        Your last estimate was{" "}
        <span className="text-[#10B981]">{amount}/yr</span> in savings.
      </>
    ),
    sub: "Pick up where you left off and lock these savings in.",
  };
}

// Default headline (no estimate yet)
function defaultHeadline(state) {
  const vsNational = state.avg - NATIONAL_AVG;
  const cheaper = vsNational < 0;
  return {
    eyebrow: null, // caller renders eyebrow with city/state
    headline: (
      <>
        Auto insurance in {state.name} averages{" "}
        <span className="text-[#10B981]">${state.avg.toLocaleString()}</span>/yr.
      </>
    ),
    sub: cheaper ? (
      <>
        That&apos;s{" "}
        <span className="text-white font-semibold">
          ${Math.abs(vsNational).toLocaleString()}
        </span>{" "}
        below the national average. See if you can shave more.
      </>
    ) : (
      <>
        That&apos;s{" "}
        <span className="text-white font-semibold">
          ${vsNational.toLocaleString()}
        </span>{" "}
        above the national average — drivers here save the most by shopping.
      </>
    ),
  };
}

export default function GeoFooterCard() {
  const { geo } = useGeo();
  const estimate = useLastEstimate();
  const { open: openQuoteModal } = useQuoteModal();
  const state = matchState(geo?.region);
  if (!state) return null;

  const city = matchCity(geo?.city, state.slug);
  const cityAvg = city ? Math.round(state.avg * city.mult) : null;
  const reEngage = Boolean(estimate);

  const copy = reEngage
    ? reEngageHeadline(estimate, city, state)
    : defaultHeadline(state);

  return (
    <aside
      data-testid="geo-footer-card"
      data-mode={reEngage ? "re-engage" : "default"}
      className={`mb-12 p-6 lg:p-7 border relative overflow-hidden ${
        reEngage
          ? "bg-gradient-to-br from-[#0A2540] to-[#0F3F33] border-[#10B981]/30"
          : "bg-gradient-to-br from-[#0F2F4F] to-[#0A2540] border-white/10"
      }`}
    >
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]"
      />

      {/* Dismiss button only in re-engage mode (lets visitor reset the loop) */}
      {reEngage && (
        <button
          type="button"
          onClick={clearEstimate}
          aria-label="Clear my saved estimate"
          data-testid="geo-footer-dismiss"
          className="absolute top-3 right-3 w-7 h-7 grid place-items-center text-slate-500 hover:text-slate-200 hover:bg-white/5 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981] mb-3">
            {reEngage ? (
              <Sparkles className="w-3.5 h-3.5" />
            ) : (
              <MapPin className="w-3.5 h-3.5" />
            )}
            <span data-testid="geo-footer-eyebrow">
              {reEngage
                ? copy.eyebrow
                : city
                  ? `From ${city.name}, ${state.code}`
                  : `From ${state.name}`}
            </span>
            {!reEngage && (
              <>
                <span className="text-slate-500">·</span>
                <span className="text-slate-500 normal-case tracking-normal font-medium">
                  Most viewed near you
                </span>
              </>
            )}
            {reEngage && estimate && (
              <>
                <span className="text-slate-500">·</span>
                <span
                  data-testid="geo-footer-estimate-age"
                  className="text-slate-500 normal-case tracking-normal font-medium"
                >
                  {relativeTime(estimate.ts)}
                </span>
              </>
            )}
          </div>

          <h3
            data-testid="geo-footer-headline"
            className="font-display font-black text-2xl md:text-3xl tracking-tight text-white leading-[1.1] [text-wrap:balance]"
          >
            {copy.headline}
          </h3>

          <p className="text-sm text-slate-500 mt-2 max-w-xl">{copy.sub}</p>
        </div>

        <div className="lg:col-span-5 flex flex-col gap-2.5">
          {/* In re-engage mode, primary CTA is the "Pick up" button.
              When the user's last estimate was the savings calc (not refi),
              ctaForType returns null and we open the global Quote Modal
              instead of navigating away. */}
          {reEngage ? (
            ctaForType(estimate.type) ? (
              <Link
                to={ctaForType(estimate.type)}
                data-testid="geo-footer-resume-link"
                className="group flex items-center justify-between gap-3 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold px-4 py-3 transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4" />
                  {ctaLabel(estimate.type, state.code)}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => openQuoteModal("insurance")}
                data-testid="geo-footer-resume-link"
                className="group flex items-center justify-between gap-3 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold px-4 py-3 transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4" />
                  {ctaLabel(estimate.type, state.code)}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )
          ) : null}

          <Link
            to={`/insurance/${state.slug}`}
            data-testid="geo-footer-state-link"
            className="group flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#10B981] px-4 py-3 transition-colors"
          >
            <span className="text-sm text-white font-semibold">
              {state.name} insurance guide
            </span>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#10B981] group-hover:translate-x-0.5 transition-all" />
          </Link>

          {city && (
            <Link
              to={`/insurance/${state.slug}/${city.slug}`}
              data-testid="geo-footer-city-link"
              className="group flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#10B981] px-4 py-3 transition-colors"
            >
              <span className="text-sm text-white font-semibold">
                {city.name} rates · ~${cityAvg.toLocaleString()}/yr
              </span>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#10B981] group-hover:translate-x-0.5 transition-all" />
            </Link>
          )}

          {!reEngage && (
            <button
              type="button"
              onClick={() => openQuoteModal("insurance")}
              data-testid="geo-footer-cta-link"
              className="group flex items-center justify-between gap-3 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold px-4 py-3 transition-colors"
            >
              <span className="inline-flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4" />
                Compare {state.code} offers
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
